/**
 * Full-screen native sheet for a single scenario section (mobile/native).
 *
 * Mirrors the CPT ProcedureDrilldownModal feel: slides up to full screen, grab
 * handle, swipe-down to dismiss, breadcrumb header ("Scenario › Section") with a
 * Back control, safe-area aware, z above the tab bar. A history entry is pushed
 * on open so the OS/browser back (and iOS edge-swipe → history back) closes it.
 *
 * Generic: pass any section's existing content as children. Used first for
 * Nearby Hospitals; the other heavy sections can reuse it.
 */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  dark: boolean;
  scenarioName?: string;
  sectionTitle: string;
  children: React.ReactNode;
  /** Set false when the children already supply their own padding (avoids double). */
  padded?: boolean;
  /**
   * When true, the PARENT owns history/routing (e.g. it mirrors the open section
   * in the URL as `?section=KEY`). The sheet then skips its own pushState/popstate
   * entirely — OS-back is handled by the URL change unmounting it, and the
   * back/swipe gestures just call onClose (which the parent maps to history.back).
   * This is what lets a deep link into a sub-page (e.g. /cpt/123) return here.
   */
  urlManaged?: boolean;
  /** Render above another open sheet (e.g. a code modal stacked over Code Breakdown). */
  elevated?: boolean;
}

export default function ScenarioSectionModal({ open, onClose, dark, scenarioName, sectionTitle, children, padded = true, urlManaged = false, elevated = false }: Props) {
  const startY = useRef<number | null>(null);
  const [dragY, setDragY] = useState(0);
  const closingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    closingRef.current = false;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // In urlManaged mode the parent owns the history entry (via the URL), so we
    // only lock scroll here and let the URL drive open/close.
    if (urlManaged) {
      return () => { document.body.style.overflow = prevOverflow; };
    }
    // Push a history entry so hardware/browser back + iOS edge-swipe close us.
    try { window.history.pushState({ scenarioSectionModal: true }, ""); } catch { /* ignore */ }
    const onPop = () => { closingRef.current = true; onClose(); };
    window.addEventListener("popstate", onPop);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("popstate", onPop);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const bg = dark ? "#0a1628" : "#FAF9F6";
  const headerBg = dark ? "rgba(13,30,53,0.92)" : "rgba(255,255,255,0.92)";
  const border = dark ? "rgba(197,165,90,0.25)" : "rgba(27,27,27,0.10)";
  const txt = dark ? "#f0ebe3" : "#1B1B1B";
  const muted = dark ? "rgba(240,235,227,0.6)" : "rgba(27,27,27,0.62)";
  const accent = dark ? "#C5A55A" : "#B22234";

  // Close by unwinding the history entry (so back stack stays clean); fall back
  // to a direct close if our entry isn't on top.
  const close = () => {
    if (closingRef.current) return;
    if (urlManaged) {
      // Parent maps onClose to history.back(), popping the ?section URL entry.
      onClose();
      return;
    }
    if (window.history.state?.scenarioSectionModal) {
      window.history.back();
    } else {
      onClose();
    }
  };

  const onTouchStart = (e: React.TouchEvent) => { startY.current = e.touches[0].clientY; };
  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current == null) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) setDragY(dy);
  };
  const onTouchEnd = () => {
    if (dragY > 110) close();
    setDragY(0);
    startY.current = null;
  };

  // Portal to <body> so the sheet escapes the page's nested stacking contexts
  // (otherwise its z-index only competes within the page wrapper, and the fixed
  // global header / tab bar render on top of it).
  return createPortal(
    <div
      className={`fixed inset-0 flex flex-col ${elevated ? 'z-[10070]' : 'z-[10050]'}`}
      style={{
        backgroundColor: bg,
        transform: `translateY(${dragY}px)`,
        transition: dragY ? "none" : "transform 0.28s cubic-bezier(0.22,1,0.36,1)",
        animation: dragY ? undefined : "ahp-sheet-up 0.28s cubic-bezier(0.22,1,0.36,1)",
      }}
    >
      <style>{`@keyframes ahp-sheet-up { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>

      {/* Header: grab handle + breadcrumb + back */}
      <div
        style={{ backgroundColor: headerBg, borderBottom: `1px solid ${border}`, backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", paddingTop: "env(safe-area-inset-top, 0px)" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="flex justify-center pt-2">
          <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: muted, opacity: 0.5 }} />
        </div>
        <div className="flex items-center gap-1.5 px-2 py-2.5">
          <button onClick={close} aria-label="Back" className="flex items-center -ml-1 pr-1.5 py-1 flex-shrink-0" style={{ color: accent }}>
            <ChevronLeft className="w-6 h-6" />
          </button>
          <nav className="flex items-center gap-1.5 min-w-0 flex-1" style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>
            {scenarioName && (
              <>
                <span className="truncate" style={{ fontSize: 13, color: muted, maxWidth: "44%" }}>{scenarioName}</span>
                <span style={{ fontSize: 13, color: muted }}>›</span>
              </>
            )}
            <span className="truncate" style={{ fontSize: 15, fontWeight: 700, color: txt }}>{sectionTitle}</span>
          </nav>
        </div>
      </div>

      {/* Body. overflow-x-hidden guarantees the sheet can never be panned
          sideways or have content clipped off the left edge — any genuinely wide
          child (e.g. the severity bucket strip) keeps its OWN inner
          overflow-x-auto scroll, so it stays reachable without widening the sheet. */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain" style={{ WebkitOverflowScrolling: "touch", paddingBottom: "calc(28px + env(safe-area-inset-bottom, 0px))" }}>
        <div className={`max-w-full ${padded ? "px-3 py-3" : ""}`}>{children}</div>
      </div>
    </div>,
    document.body
  );
}
